import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { loanAPI } from "../../../../services/AdminPortal/loansAPI";

const StaffworkTable = ({
  setSelectedStaffMembers,
  selectedStaffMembers,
  searchTerm,
  selectedRoleFilter,
  getDisplayProfilePicUrl,
  handleViewStaffProfile,
  ITEMS_PER_PAGE,
}) => {
  const [staffData, setStaffData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Local states for selection
  const [selectedAll, setSelectedAll] = useState(false);

  const fetchWorkPerformanceData = useCallback(
    async (pageToFetch = 1) => {
      setLoading(true);
      setError(null);
      try {
        const response = await loanAPI.getAllStaffWithLoans({
          page: pageToFetch,
          limit: ITEMS_PER_PAGE,
        });
        setStaffData(response.data || []);
        if (response.pagination && response.pagination.totalPages) {
          setCurrentPage(response.pagination.currentPage);
          setTotalPages(response.pagination.totalPages);
        } else {
          setTotalPages(1);
        }
      } catch (err) {
        setError(err.message || "Failed to fetch work performance data.");
        setStaffData([]);
      } finally {
        setLoading(false);
      }
    },
    [ITEMS_PER_PAGE]
  );

  useEffect(() => {
    fetchWorkPerformanceData(currentPage);
  }, [currentPage, fetchWorkPerformanceData]);

  const filteredAndSearchedData = useMemo(() => {
    const lowerSearch = searchTerm?.toLowerCase() || "";

    return staffData.filter(
      (staff) =>
        (staff.name?.toLowerCase().includes(lowerSearch) ||
          staff.staffId?.toLowerCase().includes(lowerSearch) ||
          staff.branchName?.toLowerCase().includes(lowerSearch)) && // ✅ Added branch search
        (selectedRoleFilter === "All" || staff.role === selectedRoleFilter)
    );
  }, [staffData, searchTerm, selectedRoleFilter]);

  // Local selection handlers
  const handleLocalSelectAll = useCallback(() => {
    const newSelectedAll = !selectedAll;
    setSelectedAll(newSelectedAll);
    if (newSelectedAll) {
      setSelectedStaffMembers(filteredAndSearchedData.map((s) => s.staffId));
    } else {
      setSelectedStaffMembers([]);
    }
  }, [selectedAll, filteredAndSearchedData]);

  const handleLocalCheckboxChange = useCallback(
    (staffId) => {
      setSelectedStaffMembers((prev) => {
        const updatedSelection = prev.includes(staffId)
          ? prev.filter((id) => id !== staffId)
          : [...prev, staffId];

        setSelectedAll(
          filteredAndSearchedData.length > 0 &&
            updatedSelection.length === filteredAndSearchedData.length
        );
        return updatedSelection;
      });
    },
    [filteredAndSearchedData]
  ); // filteredAndSearchedData is a dependency to correctly update selectedAll

  const goToPage = useCallback(
    (pageNumber) => {
      if (pageNumber >= 1 && pageNumber <= totalPages) {
        setCurrentPage(pageNumber);
      }
    },
    [totalPages]
  );

  const goToPreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  }, [totalPages]);

  if (loading) {
    return (
      <div className="min-h-[300px] bg-[#131313] text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <p className="text-center text-red-500 py-4">
        Error loading work performance: {error}
      </p>
    );
  }

  return (
    <div className="p-4">
      <div className="bg-[#131313] rounded-lg border border-[#333333] w-full overflow-x-auto scrollbar-hide">
        <table className="min-w-full">
          <thead className="border-y border-[#333333]">
            <tr className="text-white">
              <th className="p-4 text-center border-2 border-[#333333]">
                <input
                  type="checkbox"
                  className="appearance-none h-5 w-5 border-2 border-white/20 rounded-sm bg-transparent checked:transparent checked:border-yellow-400 checked:appearance-none relative cursor-pointer
            before:content-['✓'] before:absolute before:text-[#ffca20] before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                  checked={selectedAll}
                  onChange={handleLocalSelectAll}
                  disabled={filteredAndSearchedData.length === 0}
                />
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                STAFF PROFILE
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                ROLE
              </th>
              {/* ✅ New Branch Name Column */}
              <th className="p-4 text-center border-2 border-[#333333]">
                BRANCH NAME
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                TOTAL ASSIGNED
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                STATUS
              </th>
              <th className="p-4 text-center border-2 border-[#333333]">
                ACTIONS
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {filteredAndSearchedData.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center py-6 text-gray-400">
                  No staff data found matching your criteria.
                </td>
              </tr>
            ) : (
              filteredAndSearchedData.map((staff, index) => (
                <tr key={staff.staffId || index} className="text-white">
                  <td className="p-4 text-center border-2 border-[#333333]">
                    <input
                      type="checkbox"
                      className="appearance-none h-5 w-5 border-2 border-white/20 rounded-sm bg-transparent checked:bg-transparent checked:border-yellow-400 checked:appearance-none relative cursor-pointer
              before:content-['✓'] before:absolute before:text-[#ffca20] before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                      checked={selectedStaffMembers.includes(staff.staffId)}
                      onChange={() => handleLocalCheckboxChange(staff.staffId)}
                    />
                  </td>
                  <td className="p-4 border-l border-[#333333] lg:min-w-[200px]">
                    <div className="flex items-center">
                      <img
                        src={
                          staff.profilePicture || getDisplayProfilePicUrl(null)
                        }
                        alt={staff.name}
                        className="h-10 w-10 rounded-full mr-3 object-cover flex-shrink-0"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = getDisplayProfilePicUrl(null);
                        }}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-md lg:truncate">
                          {staff.name
                            .split(" ")
                            .map(
                              (word) =>
                                word.charAt(0).toUpperCase() +
                                word.slice(1).toLowerCase()
                            )
                            .join(" ")}
                        </p>
                        <p className="text-gray-400 text-sm lg:truncate">
                          {staff.staffId}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-center border-2 border-[#333333]">
                    {staff.role
                      .split(" ")
                      .map((word) => word.toUpperCase())
                      .join(" ")}
                  </td>
                  {/* ✅ New Branch Name Value */}
                  <td className="p-4 text-center border-2 border-[#333333]">
                    {staff?.branchName || "N/A"}
                  </td>
                  <td className="p-4 text-center border-2 border-[#333333]">
                    <div className="flex flex-col justify-center items-center w-full">
                      <p className="font-bold text-lg mb-2 whitespace-nowrap">
                        {staff.totalLoans !== undefined ? staff.totalLoans : 0}
                      </p>
                      <div className="flex flex-col sm:grid sm:grid-cols-3 gap-1 sm:gap-3 w-full text-center text-xs sm:text-sm">
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-400">Active</span>
                          <span className="text-[#a0a0f2] text-md font-bold">
                            {staff.newLeadsCount !== undefined
                              ? staff.newLeadsCount
                              : "0"}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-400">
                            Approved
                          </span>
                          <span className="text-green-500 font-bold">
                            {staff.approvedCount !== undefined
                              ? staff.approvedCount
                              : "0"}
                          </span>
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-400">
                            Rejected
                          </span>
                          <span className="text-red-500 font-bold">
                            {staff.rejectedCount !== undefined
                              ? staff.rejectedCount
                              : "0"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>
                  {/* <td className="p-4 text-center border-2 border-[#333333]">
                    <span className="text-lg font-bold">
                      {staff.performanceRating !== undefined
                        ? `${staff.performanceRating}%`
                        : "0%"}
                    </span>
                  </td> */}
                  <td className="p-4 border-l border-[#333333]">
                    <p
                      className={`${
                        staff?.status?.toLowerCase() === "active"
                          ? "bg-green-500 bg-opacity-20 text-green-500"
                          : staff?.status?.toLowerCase() === "pending"
                          ? "bg-orange-500 bg-opacity-20 text-orange-500"
                          : "bg-[#FFCA20] bg-opacity-20 text-[#FFCA20]"
                      } px-3 py-1 rounded-full border-none focus:outline-none`}
                    >
                      {staff?.status}
                    </p>
                  </td>
                  <td className="p-4 text-center border-2 border-[#333333]">
                    <div className="flex flex-col lg:flex-row items-center lg:justify-around lg:gap-4 gap-2">
                      {/* <button className="flex items-center gap-2 text-[#FFCA20]">
                        Icon
                        <svg
                          width="12"
                          height="16"
                          viewBox="0 0 12 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M11.4062 2.9375C11.7812 3.3125 12 3.8125 12 4.34375V14C12 15.125 11.0938 16 10 16H2C0.875 16 0 15.125 0 14V2C0 0.90625 0.875 0 2 0H7.65625C8.1875 0 8.6875 0.21875 9.0625 0.59375L11.4062 2.9375ZM10.5 14V5H8C7.4375 5 7 4.5625 7 4V1.53125H2C1.71875 1.53125 1.5 1.75 1.5 2.03125V14C1.5 14.2812 1.71875 14.5 2 14.5H10C10.25 14.5 10.5 14.2812 10.5 14ZM3 4C2.71875 4 2.5 3.78125 2.5 3.5C2.5 3.25 2.71875 3 3 3H5.5C5.75 3 6 3.25 6 3.5C6 3.78125 5.75 4 5.5 4H3ZM3 6C2.71875 6 2.5 5.78125 2.5 5.5C2.5 5.25 2.71875 5 3 5H5.5C5.75 5 6 5.25 6 5.5C6 5.78125 5.75 6 5.5 6H3ZM6.125 11.375C6.75 10.875 7.8125 11.0625 8.1875 11.8125C8.25 11.9375 8.375 12 8.5 12H9C9.25 12 9.5 12.25 9.5 12.5C9.5 12.7812 9.25 13 9 13H8.5C8 13 7.53125 12.7188 7.3125 12.2812C7.21875 12.0938 7.0625 12.0625 7 12.0625C6.90625 12.0625 6.75 12.0938 6.6875 12.25L6.4375 12.75C6.34375 12.9062 6.1875 13 6 13H5.9375C5.75 13 5.5625 12.875 5.5 12.6562L5 11.0938L4.65625 12.0938C4.46875 12.6562 3.96875 13 3.375 13H3C2.71875 13 2.5 12.7812 2.5 12.5C2.5 12.25 2.71875 12 3 12H3.375C3.53125 12 3.65625 11.9062 3.71875 11.7812L4.28125 10.0625C4.375 9.75 4.65625 9.5625 5 9.5625C5.3125 9.5625 5.59375 9.75 5.6875 10.0625L6.125 11.375Z"
                            fill="#FFCA20"
                          />
                        </svg>
                      </button> */}
                      <button
                        className="text-white underline text-sm lg:text-base"
                        onClick={() => {
                          if (staff.staffId) {
                            handleViewStaffProfile(staff.staffId);
                          }
                        }}
                        disabled={!staff.staffId}
                      >
                        View
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6 mb-4">
          <button
            onClick={goToPreviousPage}
            disabled={currentPage === 1}
            className="flex items-center text-sm text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={16} className="mr-1" />
            Previous
          </button>
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (pageNumber) => (
                <button
                  key={pageNumber}
                  onClick={() => goToPage(pageNumber)}
                  className={`w-8 h-8 flex items-center justify-center rounded ${
                    currentPage === pageNumber
                      ? "bg-[#FFCA20] text-black font-medium"
                      : "bg-gray-800 text-white hover:bg-gray-700"
                  }`}
                >
                  {pageNumber}
                </button>
              )
            )}
          </div>
          <button
            onClick={goToNextPage}
            disabled={currentPage === totalPages}
            className="flex items-center text-sm text-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <ChevronRight size={16} className="ml-1" />
          </button>
        </div>
      )}
    </div>
  );
};

export default StaffworkTable;
